<template>
  <div class="color-converter">
    <h1>颜色格式转换工具</h1>
    
    <div class="converter-container">
      <!-- 颜色选择器 -->
      <div class="color-picker-section">
        <el-color-picker
          v-model="selectedColor"
          show-alpha
          @change="handleColorChange"
        />
        <div class="color-preview" :style="{ backgroundColor: selectedColor }"></div>
      </div>

      <!-- 颜色值显示 -->
      <div class="color-values">
        <!-- HEX -->
        <div class="color-value-item">
          <span class="label">HEX:</span>
          <el-input v-model="hexColor" readonly>
            <template #append>
              <el-button @click="copyValue(hexColor)">复制</el-button>
            </template>
          </el-input>
        </div>

        <!-- RGB -->
        <div class="color-value-item">
          <span class="label">RGB:</span>
          <el-input v-model="rgbColor" readonly>
            <template #append>
              <el-button @click="copyValue(rgbColor)">复制</el-button>
            </template>
          </el-input>
        </div>

        <!-- RGBA -->
        <div class="color-value-item">
          <span class="label">RGBA:</span>
          <el-input v-model="rgbaColor" readonly>
            <template #append>
              <el-button @click="copyValue(rgbaColor)">复制</el-button>
            </template>
          </el-input>
        </div>

        <!-- HSL -->
        <div class="color-value-item">
          <span class="label">HSL:</span>
          <el-input v-model="hslColor" readonly>
            <template #append>
              <el-button @click="copyValue(hslColor)">复制</el-button>
            </template>
          </el-input>
        </div>

        <!-- HSLA -->
        <div class="color-value-item">
          <span class="label">HSLA:</span>
          <el-input v-model="hslaColor" readonly>
            <template #append>
              <el-button @click="copyValue(hslaColor)">复制</el-button>
            </template>
          </el-input>
        </div>
      </div>

      <!-- 手动输入区域 -->
      <div class="manual-input-section">
        <h3>手动输入颜色值</h3>
        <div class="input-group">
          <el-input
            v-model="manualInput"
            placeholder="输入HEX、RGB、HSL等格式的颜色值"
          >
            <template #append>
              <el-button @click="parseManualInput">转换</el-button>
            </template>
          </el-input>
        </div>
      </div>

      <!-- 说明区域 -->
      <div class="info-section">
        <h3>支持的颜色格式</h3>
        <ul>
          <li>HEX: #RGB, #RGBA, #RRGGBB, #RRGGBBAA</li>
          <li>RGB: rgb(255, 255, 255)</li>
          <li>RGBA: rgba(255, 255, 255, 1)</li>
          <li>HSL: hsl(360, 100%, 100%)</li>
          <li>HSLA: hsla(360, 100%, 100%, 1)</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { ElMessage } from 'element-plus'
import Color from 'color'

const selectedColor = ref('#409EFF')
const hexColor = ref('')
const rgbColor = ref('')
const rgbaColor = ref('')
const hslColor = ref('')
const hslaColor = ref('')
const manualInput = ref('')

// 将RGB颜色转换为HSL
const rgbToHsl = (r, g, b) => {
  r /= 255
  g /= 255
  b /= 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h, s, l = (max + min) / 2

  if (max === min) {
    h = s = 0
  } else {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break
      case g: h = (b - r) / d + 2; break
      case b: h = (r - g) / d + 4; break
    }
    h /= 6
  }

  return [
    Math.round(h * 360),
    Math.round(s * 100),
    Math.round(l * 100)
  ]
}

// 处理颜色变化
const handleColorChange = (color) => {
  if (!color) return

  try {
    // 使用 color 库解析颜色
    const colorObj = Color(color)
    const rgb = colorObj.rgb().array()
    const alpha = colorObj.alpha()

    // 更新各种格式的颜色值
    hexColor.value = color.toUpperCase()
    rgbColor.value = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`
    rgbaColor.value = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`

    const hsl = colorObj.hsl().array()
    hslColor.value = `hsl(${Math.round(hsl[0])}, ${Math.round(hsl[1])}%, ${Math.round(hsl[2])}%)`
    hslaColor.value = `hsla(${Math.round(hsl[0])}, ${Math.round(hsl[1])}%, ${Math.round(hsl[2])}%, ${alpha})`
  } catch (error) {
    ElMessage.error('无效的颜色值')
  }
}

// 复制颜色值
const copyValue = async (value) => {
  try {
    await navigator.clipboard.writeText(value)
    ElMessage.success('复制成功')
  } catch (error) {
    ElMessage.error('复制失败')
  }
}

// 解析手动输入的颜色值
const parseManualInput = () => {
  if (!manualInput.value) {
    ElMessage.warning('请输入颜色值')
    return
  }

  try {
    // 尝试解析颜色值
    Color(manualInput.value) // 验证颜色值是否有效
    selectedColor.value = manualInput.value
    handleColorChange(selectedColor.value)
    ElMessage.success('转换成功')
  } catch (error) {
    ElMessage.error('无效的颜色值')
  }
}

// 初始化
handleColorChange(selectedColor.value)
</script>

<style scoped>
.color-converter {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  margin-top: 80px;
}

.converter-container {
  margin-top: 20px;
}

.color-picker-section {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
}

.color-preview {
  width: 100px;
  height: 100px;
  border-radius: 8px;
  border: 1px solid #dcdfe6;
}

.color-values {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 30px;
}

.color-value-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.label {
  width: 60px;
  font-weight: bold;
}

.manual-input-section {
  margin: 30px 0;
}

.input-group {
  margin-top: 10px;
}

.info-section {
  margin-top: 30px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.info-section ul {
  padding-left: 20px;
  margin: 10px 0;
}

.info-section li {
  margin: 8px 0;
  line-height: 1.5;
}

:deep(.el-input-group__append) {
  padding: 0;
}

:deep(.el-input-group__append button) {
  border: none;
  margin: 0;
  height: 100%;
}

@media (max-width: 768px) {
  .color-value-item {
    flex-direction: column;
    align-items: stretch;
  }

  .label {
    width: auto;
  }
}
</style> 